import { useContext } from 'react';
import useSWR from 'swr';
import { UseSWRMutationResult } from '../../common/types/swrTypes';
import { deleteToApi, getFromApi, postStringToApi, putToApi } from '../../common/utils/apiHelper';
import { ConfigContext } from '../configuration/ConfigContext';
import { usePropertyAssociationContext } from '../propertyAssociation/PropertyAssociationContext';
import { UpdateMemberRestrictionsRequest } from './modals/UpdateMemberRestrictionsRequest';
import { GetFilteredMembersResponse } from './models/GetMembersResponse';
import { GetMembersResponse } from './models/Member';

type UseMembersResult = UseSWRMutationResult<GetMembersResponse, 'response'>;

export const useMembers = (): UseMembersResult => {
    const { apiBaseUrl } = useContext(ConfigContext);
    const { currentPropertyAssociationId } = usePropertyAssociationContext();

    const url = currentPropertyAssociationId ? `${apiBaseUrl}/propertyassociations/${currentPropertyAssociationId}/members` : null;

    const { data, error, isLoading, mutate } = useSWR<GetMembersResponse>(url);

    return {
        response: data,
        isLoading,
        error,
        refetch: () => mutate(),
    };
};

type UsePendingMembersResult = UseSWRMutationResult<GetMembersResponse, 'response'>;

export const usePendingMembers = (): UsePendingMembersResult => {
    const { apiBaseUrl } = useContext(ConfigContext);
    const { currentPropertyAssociationId } = usePropertyAssociationContext();

    const url = currentPropertyAssociationId ? `${apiBaseUrl}/propertyassociations/${currentPropertyAssociationId}/members/pending` : null;

    const { data, error, isLoading, mutate } = useSWR<GetMembersResponse>(url);

    return {
        response: data,
        isLoading,
        error,
        refetch: () => mutate(),
    };
};

export const getFilteredMembers = (apiBaseUrl: string, propertyAssociationId: string, onSuccess: (data: GetFilteredMembersResponse) => void, onFail: () => void): void => {
    getFromApi(`${apiBaseUrl}/propertyassociations/${propertyAssociationId}/members/filtered`, onSuccess, onFail);
};

export const addMember = (apiBaseUrl: string, propertyAssociationId: string, userId: string, onSuccess: () => void, onFail: () => void): void => {
    postStringToApi(`${apiBaseUrl}/propertyassociations/${propertyAssociationId}/members`, userId, onSuccess, onFail);
};

export const removeMember = (apiBaseUrl: string, propertyAssociationId: string, userId: string, onSuccess: () => void, onFail: () => void): void => {
    deleteToApi(`${apiBaseUrl}/propertyassociations/${propertyAssociationId}/members/${userId}`, onSuccess, onFail);
};

export const updateMemberRestrictions = (
    apiBaseUrl: string,
    propertyAssociationId: string,
    userId: string,
    advertAccess: boolean,
    assetAccess: boolean,
    onSuccess: () => void,
    onFail: () => void,
): void => {
    const data: UpdateMemberRestrictionsRequest = { advertAccess, assetAccess };
    putToApi(`${apiBaseUrl}/propertyassociations/${propertyAssociationId}/members/${userId}/restrictions`, data, onSuccess, onFail);
};
